import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { UserPlus, CheckCircle, Copy, AlertCircle } from 'lucide-react';
import api from '../../../services/api';

const SeedTestParticipantTool = () => {
  const [survey, setSurvey] = useState('');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const runSeed = async () => {
    if (!survey.trim()) {
      toast.error('Survey id is required');
      return;
    }

    setLoading(true);
    setSummary(null);

    try {
      const body = {
        survey: survey.trim(),
        email: email.trim() || undefined,
        pin: pin.trim() || undefined,
      };
      const { data } = await api.post('/api/dev/seed-test-participant/', body);
      setSummary(data);
      toast.success(`Participant ${data.participant.email} ready — ${data.windows_generated} windows generated`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Seed failed: ${detail}`);
      setSummary({ error: detail });
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-primary-600" />
          Seed Test Participant
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Creates a fresh participant wired to the shared SEED-TEST sponsor/study/site and
          scheduled to take the given survey. Reuses infrastructure across runs, generates
          a fresh participant and schedule each time. Blocked in production.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Survey ID <span className="text-red-600">*</span>
        </label>
        <input
          type="text"
          value={survey}
          onChange={(e) => setSurvey(e.target.value)}
          placeholder="Survey id (the published survey backing your assessment)"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email (optional)
          </label>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Leave blank for a generated seed-*@example.com"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            PIN (optional)
          </label>
          <input
            type="text"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Default: 0000"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      <button
        onClick={runSeed}
        disabled={loading}
        className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Seeding…' : 'Create test participant'}
      </button>

      {summary && !summary.error && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded space-y-3">
          <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Participant ready — {summary.windows_generated} window{summary.windows_generated === 1 ? '' : 's'} generated
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3 space-y-2">
            <CredRow label="Email" value={summary.participant.email} onCopy={copy} />
            <CredRow label="PIN" value={summary.participant.pin} onCopy={copy} mono />
            <CredRow label="Participant ID" value={summary.participant.participant_id} onCopy={copy} mono />
          </div>

          <details className="text-xs text-gray-600 dark:text-gray-400">
            <summary className="cursor-pointer select-none">Infrastructure & IDs</summary>
            <div className="mt-2 pl-2 space-y-1 font-mono">
              <div>Survey: {summary.survey.name} ({summary.survey.id})</div>
              <div>Sponsor: {summary.infrastructure.sponsor_id}</div>
              <div>Study: {summary.infrastructure.study_code} ({summary.infrastructure.study_id})</div>
              <div>Site: {summary.infrastructure.site_code} ({summary.infrastructure.site_id})</div>
              <div>Schedule: {summary.infrastructure.schedule_id}</div>
              <div>ParticipantSchedule: {summary.infrastructure.participant_schedule_id}</div>
              <div>Participant UUID: {summary.participant.uuid}</div>
            </div>
          </details>
        </div>
      )}

      {summary?.error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>{summary.error}</div>
        </div>
      )}
    </div>
  );
};

const CredRow = ({ label, value, onCopy, mono }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="text-sm">
      <span className="text-gray-500 dark:text-gray-400">{label}: </span>
      <span className={`text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
    <button
      onClick={() => onCopy(value, label)}
      className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
      title={`Copy ${label}`}
    >
      <Copy className="w-4 h-4" />
    </button>
  </div>
);

export default SeedTestParticipantTool;
